import axios from 'axios';
import _ from 'lodash';
import store from 'app/store';
import { getProducersQuery, getLastPickupsQuery, getHaulingCompaniesQuery, getDriversQuery } from './queries';
import { getTimeSinceLastPickup, getBulkQueryData } from './utils';
import { arrayToMap } from '../../utils';
import { requiresProducerBusinessData } from './utils/regionUtils';

const getProducerListDetails = ({ filterDeleted = true, filterInactive, includePickups, filterSplitProducer, fields } = {}) =>
    new Promise((resolve, reject) => {
        const { region } = store.getState().persisted.auth.user.data;
        const queryProducers = getProducersQuery({
            filterDeleted,
            filterInactive,
            filterSplitProducer,
            fields,
        });

        const queryHaulingCompanies = getHaulingCompaniesQuery({ fields: ['id', 'full_name', 'hauler_number', 'name'] });

        const queries = [axios.get(queryProducers), axios.get(queryHaulingCompanies)];

        if (includePickups) {
            const queryPickups = getLastPickupsQuery();
            queries.push(axios.get(queryPickups));
            const queryDrivers = getDriversQuery({ fields: ['id', 'hauling_id'] });
            queries.push(axios.get(queryDrivers));
        }

        Promise.all(queries)
            .then(async (response) => {
                let producers = _.orderBy(response[0].data, ['name'], ['asc']);
                const haulers = _.reduce(
                    response[1].data,
                    (map, hauler) => {
                        return { ...map, [hauler.id]: hauler };
                    },
                    {}
                );

                let pickups = {};
                let drivers = {};

                // Only get producer business data if required for this region
                const needsProducerBusinessData = requiresProducerBusinessData(region);

                if (needsProducerBusinessData) {
                    // Extract producer business IDs from the producers data
                    const producerBusinessIds = _.filter(_.uniq(_.map(producers, 'producer_business')), (id) => id != null);

                    if (producerBusinessIds.length > 0) {
                        // Fetch producer business details
                        const producerBusinesses = await getBulkQueryData(getProducersQuery, {
                            fields: ['id', 'license_number', 'name'],
                            ids: producerBusinessIds,
                        });

                        // Create a map of producer businesses by ID
                        const producerBusinessMap = arrayToMap(producerBusinesses, 'id');

                        // Add producer business details to each producer
                        producers = producers.map((producer) => {
                            const producerBusinessId = producer.producer_business;
                            if (producerBusinessId) {
                                const producerBusiness = producerBusinessMap[producerBusinessId];
                                if (producerBusiness) {
                                    return {
                                        ...producer,
                                        producer_business_name: producerBusiness.name || '',
                                        producer_business_license_number: producerBusiness.license_number || '',
                                    };
                                }
                            }
                            return producer;
                        });
                    }
                }

                if (includePickups) {
                    pickups = _.reduce(
                        response[2].data,
                        (map, pickup) => {
                            return { ...map, [pickup.producer_id]: pickup };
                        },
                        {}
                    );

                    drivers = _.reduce(
                        response[3].data,
                        (map, driver) => {
                            return { ...map, [driver.id]: driver };
                        },
                        {}
                    );
                }

                producers = _.map(producers, (producer) => {
                    const p = { ...producer };

                    if (includePickups && pickups[producer.id]) {
                        p.pickup_date = pickups[producer.id].created_at;
                        p.pickup_days = getTimeSinceLastPickup(pickups[producer.id]);

                        const driver = drivers[pickups[producer?.id]?.driver_id];
                        p.hauler_num = haulers[driver?.hauling_id]?.hauler_number;
                        p.hauler_name = haulers[driver?.hauling_id]?.full_name;
                    }

                    if (p.default_hauling_company && haulers[p.default_hauling_company]) {
                        p.default_hauler_name = haulers[p.default_hauling_company]?.name || '-';
                    }

                    return p;
                });

                resolve({ producers });
            })

            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.log(errors);
                reject(new Error('Unable to process request'));
            });
    });

export default getProducerListDetails;
